import { Button, Typography } from '@/components/atoms';
import { ChevronDownIcon, ReceiptIcon } from '@/components/atoms/icons';
import { Accordion, PaymentItem } from '@/components/molecules';
import { PaymentsEmptyState } from '@/components/molecules/PaymentsEmptyState';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import { getUpcomingTabBadgeInfo } from '@/modules/payments/utils/installmentUtils';
import React, { useMemo, useState } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

export interface PaymentItemData {
    id: string;
    title: string;
    amount: number;
    dueDate: string;
    status: 'paid' | 'missed' | 'due-soon' | 'in-process' | 'future';
    type?: 'property' | 'community';
}

export interface PropertyPaymentsAccordionProps {
    payments: PaymentItemData[];
    /**
     * Profile unit `isOnlinePaymentEnabled` for this property.
     * False hides selection checkboxes and Pay selected.
     */
    onlinePaymentEnabled?: boolean;
    onPaySelected?: (selectedPaymentIds: string[]) => void;
    onViewAll?: (tab: 'upcoming' | 'history') => void;
}

export function PropertyPaymentsAccordion({
    payments,
    onlinePaymentEnabled = true,
    onPaySelected,
    onViewAll,
}: Readonly<PropertyPaymentsAccordionProps>) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<'upcoming' | 'history'>('upcoming');
    // Removed community filter - always show property payments only
    const [visibleCount, setVisibleCount] = useState(3); // Show 3 items initially

    const missedPayments = payments.filter(p => p.status === 'missed');
    const inProcessPayments = payments.filter(p => p.status === 'in-process');
    const paidPayments = payments.filter(p => p.status === 'paid');
    const dueSoonPayments = payments.filter(p => p.status === 'due-soon');
    const futurePayments = payments.filter(p => p.status === 'future');

    // Calculate filtered payments for current tab - only show property payments (community filter removed)
    const filteredUpcomingPayments = React.useMemo(() => {
        return [...missedPayments, ...dueSoonPayments, ...futurePayments]
            .filter(p => (p.type || 'property') === 'property');
    }, [missedPayments, dueSoonPayments, futurePayments]);

    const filteredHistoryPayments = React.useMemo(() => {
        return [...inProcessPayments, ...paidPayments]
            .filter(p => (p.type || 'property') === 'property');
    }, [inProcessPayments, paidPayments]);

    // Check if there are any payments to show (for hiding buttons when empty)
    const hasUpcomingPayments = filteredUpcomingPayments.length > 0;
    const hasHistoryPayments = filteredHistoryPayments.length > 0;
    // For upcoming tab: only show buttons when there are payments
    // For history tab: always show "Account statement" button, "View all" only when there are payments
    const shouldShowButtons = activeTab === 'upcoming'
        ? hasUpcomingPayments
        : true; // Always show buttons for history tab (Account statement is always visible)

    // Calculate totals for display
    const totalMissed = missedPayments.length;
    const totalDueSoon = dueSoonPayments.length;

    // Calculate badge info using utility function (same logic as PropertyPaymentsScreen)
    const badgeInfo = useMemo(() => {
        return getUpcomingTabBadgeInfo(
            totalMissed,
            totalDueSoon,
            colors.bloomRed,
            UI_PRIMITIVES.yellowDark
        );
    }, [totalMissed, totalDueSoon, colors.bloomRed]);

    const handlePaymentSelect = (paymentId: string) => {
        setSelectedPayments(prev =>
            prev.includes(paymentId)
                ? prev.filter(id => id !== paymentId)
                : [...prev, paymentId]
        );
    };

    // Reset visible count when switching tabs
    React.useEffect(() => {
        setVisibleCount(3);
    }, [activeTab]);

    return (
        <Accordion
            icon={ReceiptIcon}
            defaultExpanded={Platform.OS === 'web'} // Expanded by default on web per user requirement
            title={t('propertyDetails.payments')}
            subtitle={`${totalMissed} ${t('propertyDetails.missed')}, ${totalDueSoon} ${t('propertyDetails.dueIn30Days')}`}
            contentPadding={16}
        >
            <View style={styles.content}>
                <View style={styles.tabs}>
                    <TouchableOpacity
                        style={[
                            styles.tab,
                            activeTab === 'upcoming' && styles.activeTab
                        ]}
                        onPress={() => setActiveTab('upcoming')}
                    >
                        <Typography
                            variant="headline"
                            color={activeTab === 'upcoming' ? 'primary' : 'tertiary'}
                            style={styles.tabText}
                        >
                            {t('propertyDetails.upcoming')}
                        </Typography>
                        {badgeInfo.show && (
                            <View style={[
                                styles.badge,
                                { backgroundColor: badgeInfo.color }
                            ]}>
                                <Typography variant="footnote" color="white">
                                    {badgeInfo.count}
                                </Typography>
                            </View>
                        )}
                    </TouchableOpacity>

                    <TouchableOpacity
                        style={[
                            styles.tab,
                            activeTab === 'history' && styles.activeTab
                        ]}
                        onPress={() => setActiveTab('history')}
                    >
                        <Typography
                            variant="headline"
                            color={activeTab === 'history' ? 'primary' : 'tertiary'}
                            style={styles.tabText}
                        >
                            {t('propertyDetails.history')}
                        </Typography>
                    </TouchableOpacity>
                </View>

                {/* Community filter tab removed - only showing property payments */}

                <Typography variant="headline" color="primary" style={styles.summaryText}>
                    {activeTab === 'history'
                        ? t('propertyDetails.paymentHistory')
                        : t('propertyDetails.missedPayments').replace('{count}', totalMissed.toString())}
                </Typography>

                <View style={styles.paymentsList}>
                    {activeTab === 'upcoming' ? (
                        <>
                            {(() => {
                                // Show only visible items
                                const visiblePayments = filteredUpcomingPayments.slice(0, visibleCount);

                                return (
                                    <>
                                        {visiblePayments.length > 0 ? (
                                            visiblePayments.map((payment) => (
                                                <PaymentItem
                                                    key={payment.id}
                                                    id={payment.id}
                                                    title={payment.title}
                                                    amount={payment.amount}
                                                    dueDate={payment.dueDate}
                                                    status={payment.status}
                                                    type={payment.type || 'property'}
                                                    isSelected={selectedPayments.includes(payment.id)}
                                                    showPropertyName={false}
                                                    onlinePaymentEnabled={onlinePaymentEnabled}
                                                    {...(onlinePaymentEnabled
                                                        ? { onSelect: handlePaymentSelect }
                                                        : {})}
                                                />
                                            ))
                                        ) : (
                                            <PaymentsEmptyState type="property" />
                                        )}
                                        {/* Show "+ x payments due in 30 days" only if there are dueSoonPayments */}
                                        {dueSoonPayments.filter(p => (p.type || 'property') === 'property').length > 0 && (
                                            <Typography variant="subhead" color="tertiary" style={styles.moreText}>
                                                {t('propertyDetails.paymentsDueIn30Days').replace('{count}', dueSoonPayments.filter(p => (p.type || 'property') === 'property').length.toString())}
                                            </Typography>
                                        )}
                                    </>
                                );
                            })()}
                        </>
                    ) : (
                        <>
                            {(() => {
                                // Show only visible items
                                const visiblePayments = filteredHistoryPayments.slice(0, visibleCount);

                                return (
                                    <>
                                        {visiblePayments.length > 0 ? (
                                            visiblePayments.map((payment) => (
                                                <PaymentItem
                                                    key={payment.id}
                                                    id={payment.id}
                                                    title={payment.title}
                                                    amount={payment.amount}
                                                    dueDate={payment.dueDate}
                                                    status={payment.status}
                                                    type={payment.type || 'property'}
                                                    showPropertyName={false}
                                                />
                                            ))
                                        ) : (
                                            <PaymentsEmptyState variant="history" />
                                        )}
                                    </>
                                );
                            })()}
                        </>
                    )}
                </View>

                {/* View more button - only show if there are more than 3 items and not all are visible */}
                {(() => {
                    const filteredPayments = activeTab === 'upcoming'
                        ? [...missedPayments, ...dueSoonPayments, ...futurePayments]
                            .filter(p => (p.type || 'property') === 'property')
                        : [...inProcessPayments, ...paidPayments]
                            .filter(p => (p.type || 'property') === 'property');

                    const hasMore = filteredPayments.length > visibleCount;

                    return hasMore ? (
                        <TouchableOpacity
                            style={styles.viewMoreButton}
                            onPress={() => setVisibleCount(prev => Math.min(prev + 3, filteredPayments.length))}
                        >
                            <Typography variant="subhead" color="primary" style={styles.viewMoreText}>
                                {t('propertyDetails.viewMore')}
                            </Typography>
                            <ChevronDownIcon size={18} color={colors.fontPrimary} />
                        </TouchableOpacity>
                    ) : null;
                })()}

                {shouldShowButtons && (
                    <View style={styles.actionButtons}>
                        {activeTab === 'history' ? (
                            <>
                                {/* <Button
                                    title={t('propertyDetails.accountStatement')}
                                    variant="outline"
                                    onPress={() => logger.debug('PropertyPaymentsAccordion', 'Account statement')}
                                    fullWidth={false}
                                    style={styles.accountStatementButton}
                                /> */}
                                {hasHistoryPayments && (
                                    <Button
                                        title={t('propertyDetails.viewAll')}
                                        variant="outline"
                                        onPress={() => onViewAll?.('history')}
                                        fullWidth={false}
                                        style={styles.viewAllButton}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <Button
                                    title={t('propertyDetails.viewAll')}
                                    variant="outline"
                                    onPress={() => onViewAll?.('upcoming')}
                                    fullWidth={false}
                                    style={styles.actionButton}
                                />
                                {onlinePaymentEnabled && (
                                <Button
                                    title={t('propertyDetails.paySelected')}
                                    variant="primary"
                                    onPress={() => onPaySelected?.(selectedPayments)}
                                    disabled={selectedPayments.length === 0}
                                    fullWidth={false}
                                    style={styles.actionButton}
                                />
                                )}
                            </>
                        )}
                    </View>
                )}
            </View>
        </Accordion>
    );
}

const styles = StyleSheet.create({
    content: {
        paddingBottom: 16,
        gap: 12,
    },
    tabs: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ededed',
    },
    tab: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 8,
        paddingHorizontal: 10,
    },
    activeTab: {
        borderBottomWidth: 4,
        borderBottomColor: '#333333',
        marginBottom: -2, // Compensate for border to keep alignment consistent
    },
    tabText: {
        fontWeight: '600',
    },
    badge: {
        width: 18,
        height: 18,
        borderRadius: 9,
        justifyContent: 'center',
        alignItems: 'center',
    },
    summaryText: {
        fontWeight: '600',
    },
    paymentsList: {
        gap: 8,
    },
    moreText: {
        textAlign: 'center',
    },
    viewMoreButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderRadius: 4,
    },
    viewMoreText: {
        fontWeight: '500',
        textDecorationLine: 'underline',
    },
    actionButtons: {
        flexDirection: 'row',
        gap: 16,
    },
    actionButton: {
        flex: 1,
        minWidth: 0, // Ensure flex works properly
        alignSelf: 'stretch', // Override Button's default alignSelf: 'flex-start'
        height: 48, // Fixed height to match Figma
    },
    accountStatementButton: {
        flex: 1,
    },
    viewAllButton: {
        flexShrink: 0,
        paddingHorizontal: 32,
    },
});

export default PropertyPaymentsAccordion;
